import React, { useState, useEffect } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'
import { ListItemLinkButton } from '../ListItemLinkButton'
import { Divider } from 'react-native-elements'
import spacing from '../../styles/spacing'
import shared from '../../styles/shared'
import MSFESButton from '../MSFESButton'
import colors from '../../styles/colors'
import { viewMode } from '../../screens/EntityAddScreen'
import MSFESLabel from '../MSFESLabel'
import { FieldPicker } from './FieldPicker'
import EntityField from '../EntityField'
import { useSelector } from 'react-redux'

export const ValueToTenantMapper = React.forwardRef((props, ref) => {
  const { mode, xmlFields } = props

  const tenantsListRoot = useSelector(
    (state) => state?.tenants?.tenants ?? { data: [] }
  )

  const [localValue, setLocalValue] = useState(props.value)

  useEffect(() => {
    setLocalValue(props.value)
  }, [props.value])

  const setLocalValueAndUpdate = (updater) => {
    setLocalValue((_localValue) => {
      const newLocalValue = updater(_localValue)
      props.onChange(newLocalValue)
      return newLocalValue
    })
  }

  const onAddMappedFieldPressed = () => {
    const mappedValues = [...(localValue?.valueToTenantMap ?? [])]
    mappedValues.push({ match: null, tenant: null, offline_id: uuidv4() })

    setLocalValue((_localValue) => ({
      ..._localValue,
      valueToTenantMap: mappedValues
    }))
  }

  const deleteMapRow = (mappedFieldSet, index) => {
    const mappedFields = [...(localValue?.valueToTenantMap ?? [])]
    mappedFields.splice(index, 1)

    setLocalValueAndUpdate((_localValue) => ({
      ..._localValue,
      valueToTenantMap: [...mappedFields]
    }))
  }

  const textSymbol = [
    {
      backgroundColor: colors.inputBottomBorder,
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 4
    }
  ]

  return (
    <View style={[shared.debugOff, { flexDirection: 'column' }]}>
      <View>
        <MSFESLabel label={'Tenant Mapping Field'} required={true} />

        <FieldPicker
          fieldList={xmlFields}
          placeholder="Source Field"
          editable={props.mode !== viewMode}
          value={localValue?.tenantMappingField}
          onFieldSelected={(value) => {
            setLocalValueAndUpdate((_localValue) => ({
              ..._localValue,
              tenantMappingField: value
            }))
          }}
        />
      </View>
      {(localValue?.valueToTenantMap ?? []).map((mappedFieldSet, index) => {
        const sourceValue = mappedFieldSet.match
        const targetValue = mappedFieldSet.tenant

        const tenantName = tenantsListRoot.data.find(
          (tenant) => tenant.id === targetValue
        )?.name

        return (
          <View
            key={index}
            style={[
              {
                flexDirection: 'row',
                flex: 1,
                borderTopWidth: StyleSheet.hairlineWidth,
                borderColor: colors.lightestGray,
                paddingTop: spacing.m0_5
              },
              shared.debugOff
            ]}
          >
            {sourceValue && targetValue && (
              <View style={{ flex: 1, justifyContent: 'center' }}>
                <Text>
                  <Text style={textSymbol}>{sourceValue}</Text> maps to{' '}
                  <Text style={textSymbol}>
                    {targetValue} ({tenantName})
                  </Text>
                </Text>
              </View>
            )}
            {!(sourceValue && targetValue) && (
              <View>
                <EntityField
                  name={'match'}
                  field={{
                    title: 'Match',
                    field_data: { property: 'match', type: 'single-text' }
                  }}
                  errorMessage={''}
                  showLabel={false}
                  mode={props.mode}
                  entity={{ match: mappedFieldSet.match }}
                  fieldChanged={(field, newValue, isUserInitiated) => {
                    if (isUserInitiated) {
                      setLocalValueAndUpdate((_localValue) => ({
                        ..._localValue,
                        valueToTenantMap: _localValue.valueToTenantMap.map(
                          (value) => {
                            if (
                              value.offline_id === mappedFieldSet.offline_id
                            ) {
                              return { ...value, match: newValue }
                            }
                            return value
                          }
                        )
                      }))
                    }
                  }}
                />
                <EntityField
                  name={'tenant'}
                  field={{
                    title: 'Tenant',
                    field_data: { property: 'tenant', type: 'select-tenant' }
                  }}
                  errorMessage={''}
                  showLabel={false}
                  mode={props.mode}
                  fieldChanged={(field, newValue, isUserInitiated) => {
                    if (isUserInitiated) {
                      setLocalValueAndUpdate((_localValue) => ({
                        ..._localValue,
                        valueToTenantMap: _localValue.valueToTenantMap.map(
                          (value) => {
                            if (
                              value.offline_id === mappedFieldSet.offline_id
                            ) {
                              return { ...value, tenant: newValue }
                            }
                            return value
                          }
                        )
                      }))
                    }
                  }}
                />
              </View>
            )}

            {mode !== viewMode && (
              <View>
                <MSFESButton
                  icon={{ type: 'font-awesome', name: 'trash' }}
                  type="delete"
                  onPress={() => deleteMapRow(mappedFieldSet, index)}
                ></MSFESButton>
              </View>
            )}
            <Divider />
          </View>
        )
      })}
      {mode !== viewMode && (
        <ListItemLinkButton
          title={`Add Tenant/Value Mapping`}
          type="add"
          onPress={onAddMappedFieldPressed}
        />
      )}
    </View>
  )
})
